import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Callout,
  EmptyState,
  Icon,
  Illustration,
  Link,
  RowsSkeletonLoader,
} from '@node-space/ui-components'
import { PageHeader } from 'components/header/PageHeader'
import { PaymentLimitsTable } from 'components/PaymentLimits/PaymentLimitsTable'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useGetPaymentLimits } from 'hooks/queries/useGetPaymentLimits'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { useSearchablePagination } from 'hooks/useSearchablePagination'
import { LimitLevel, PaymentLimitSheetType } from 'types/paymentLimits'
import { PaymentLimitSheet } from './components/PaymentLimitSheet'

export const PaymentLimits = () => {
  const { t } = useTranslation()
  const { currentAccount } = useAccountsContext()
  const permissions = useMappedRoles()
  const canEditPaymentLimits = permissions?.paymentLimits?.canEdit ?? false
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    useSearchablePagination(MIN_PAGINATION_SIZE)

  const [isAddSheetOpen, setIsAddSheetOpen] = useState(false)

  const {
    data: limitsData,
    isFetching,
    isError,
  } = useGetPaymentLimits({
    accountId: currentAccount?.reference,
    pageNumber: page,
    pageSize: rowsPerPage,
    level: LimitLevel.ALL_CUSTOMERS,
  })

  const limits = limitsData?.content ?? []
  const totalElements = limitsData?.totalElements ?? 0

  const hasLimits = limits?.length > 0
  const showEmptyState = !isFetching && !isError && limits?.length === 0

  return (
    <>
      <PageHeader
        description={t('paymentLimits.pageDescription')}
        {...(canEditPaymentLimits && {
          renderRightSection: (
            <Button
              testId="add-limit-button"
              variant="secondary"
              iconElement={<Icon name="Plus" />}
              onClick={() => setIsAddSheetOpen(true)}
            >
              {t('paymentLimits.addLimit')}
            </Button>
          ),
        })}
      />

      <Box paddingB={24}>
        <Callout>
          {t('paymentLimits.customerLimitsCallout')}{' '}
          <Link href="/customers" target="_self">
            {t('paymentLimits.customerLimitsCalloutLink')}
          </Link>
        </Callout>
      </Box>

      {isFetching && <RowsSkeletonLoader rows={3} />}

      {hasLimits && (
        <PaymentLimitsTable
          limits={limits}
          tableName="PaymentLimitsTable"
          page={page}
          rowsPerPage={rowsPerPage}
          totalElements={totalElements}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />
      )}

      {showEmptyState && (
        <EmptyState
          testId="payment-limits-empty-state"
          LottiePlayer={<Illustration name="Welcome" />}
          title={t('paymentLimits.emptyStateTitle')}
          description={t('paymentLimits.emptyStateDescription')}
          {...(canEditPaymentLimits && {
            PrimaryButton: (
              <Button
                variant="secondary"
                iconElement={<Icon name="Plus" />}
                onClick={() => setIsAddSheetOpen(true)}
              >
                {t('paymentLimits.addLimit')}
              </Button>
            ),
          })}
        />
      )}

      <PaymentLimitSheet
        isOpen={isAddSheetOpen}
        type={PaymentLimitSheetType.ADD}
        onClose={() => setIsAddSheetOpen(false)}
      />
    </>
  )
}
